<template>
  <div id="checkout" class="flex-full">
    <div class="checkout-title py5 px20 background" v-if="!showThankYouPage" v-background-image="image">
      <div class="container">
        <div class="row center-xs">
          <h1 class="h4 my20 bold">
            {{ $t('Checkout') }}
          </h1>
        </div>
      </div>
    </div>
    <div class="container" style="min-height: 100vh" v-if="renderCheckout && !showThankYouPage">
      <div class="row center-xs">
        <div class="col-sm-10 col-md-7 col-xs-12 pb20">
          <personal-details class="line relative" :focused-field="focusedField" />
          <shipping class="shipping line relative" v-if="isNotPickup" />
          <div v-for="group in getGroupedByPartnerProducts" style="margin: 25px 0" :key="group.partner.code">
            <delivery-time
              class="delivery line relative"
              :group="group"
              :shipping="shipping"
            />
            <cutlery-qty
              v-if="group.partner.display_mode === 'food' && (group.partner.hasOwnProperty('cutlery_availability') && group.partner.cutlery_availability)"
              :partner-code="group.partner.code"
            />
            <gift-comment :partner-code="group.partner.code" v-if="group.partner.code === 'gift'" />
            <comment :partner-code="group.partner.code" v-else-if="group.partner.location_code !== 'lutsk'" />
          </div>
          <payment class="payment line relative" :shipping-methods="shippingMethods" />
        </div>
        <order-review class="order-review line relative" :shipping-methods="shippingMethods"
                      :shipping="shipping"
                      :is-card="payment.paymentMethod === 'ipay_masterpass'"
        />
        <div id="custom-steps" />
      </div>
    </div>
    <thank-you-page v-if="showThankYouPage && renderCheckout" />
    <HelpProblems v-if="renderCheckout" />
    <bounce-loader style="height: 100vh; margin-bottom: 40px" v-else />
  </div>
</template>

<script async>
import backgroundImage from 'theme/directives/background-image';
import Checkout from '@vue-storefront/core/pages/Checkout';

const BounceLoader = () => import(/* webpackChunkName: "vsf-bounce-loader" */ 'theme/components/theme/blocks/Home/Preloaders/BounceLoader');
const HelpProblems = () => import(/* webpackChunkName: "vsf-help-problems" */ 'theme/components/core/blocks/Checkout/HelpProblems');
const DeliveryTime = () => import(/* webpackChunkName: "vsf-delivery-time" */ 'theme/components/core/blocks/Checkout/DeliveryTime');
const ThankYouPage = () => import(/* webpackChunkName: "vsf-thank-you-page" */ 'theme/components/core/blocks/Checkout/ThankYouPage');
const OrderReview = () => import(/* webpackChunkName: "vsf-order-review" */ 'theme/components/core/blocks/Checkout/OrderReview');
const Payment = () => import(/* webpackChunkName: "vsf-payment" */ 'theme/components/core/blocks/Checkout/Payment');
const Shipping = () => import(/* webpackChunkName: "vsf-shipping" */ 'theme/components/core/blocks/Checkout/Shipping');
const PersonalDetails = () => import(/* webpackChunkName: "vsf-personal-details" */ 'theme/components/core/blocks/Checkout/PersonalDetails');
const CutleryQty = () => import(/* webpackChunkName: "vsf-cutlery-qty" */ 'theme/components/core/blocks/Checkout/CutleryQty');
const GiftComment = () => import(/* webpackChunkName: "vsf-gift-comment" */ 'theme/components/core/blocks/Checkout/GiftComment');
const Comment = () => import(/* webpackChunkName: "vsf-comment" */ 'theme/components/core/blocks/Checkout/Comment');

export default {
  components: {
    Comment,
    GiftComment,
    CutleryQty,
    PersonalDetails,
    Shipping,
    DeliveryTime,
    Payment,
    OrderReview,
    ThankYouPage,
    HelpProblems,
    BounceLoader
  },
  data () {
    return {
      image: '../assets/bg.png'
    }
  },
  directives: {
    backgroundImage
  },
  mixins: [Checkout],
  methods: {
    notifyEmptyCart () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('Shopping cart is empty. Please add some products before entering Checkout'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyOutStock (chp) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: chp.name + this.$t(' is out of the stock!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyNotAvailable () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('Some of the ordered products are not available!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyNotValid (message = null) {
      message = message || 'Some of the form fields are not valid!'
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t(message),
        action1: { label: this.$t('OK') }
      })
    },
    incorrectDeliveryTime (partner) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('The selected delivery time range is not available. Please select another. Store: ') + partner,
        action1: { label: this.$t('OK') }
      })
    },
    incorrectAsapTime (partner) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('ASAP delivery time is expired for Store: ') + partner,
        action1: { label: this.$t('OK') }
      })
    },
    notifyStockCheck () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('Stock check in progress, please wait while available stock quantities are checked'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyNoConnection () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('There is no Internet connection. You can still place your order. We will notify you if any of ordered products is not available because we cannot check it right now.'),
        action1: { label: this.$t('OK') }
      })
    }
  }
}
</script>

<style lang="scss">
  @import '~theme/css/base/text';
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';
  $bg-secondary: color(secondary, $colors-background);
  $color-tertiary: color(tertiary);
  $color-secondary: color(secondary);
  $color-error: color(error);
  $color-white: color(white);
  $color-black: color(black);

  #checkout {
    .number-circle {
      width: 35px;
      height: 35px;

      @media (max-width: 768px) {
        width: 30px;
        height: 30px;
        line-height: 30px;
      }
    }
    .radioStyled {
      display: block;
      position: relative;
      padding-left: 35px;
      margin-bottom: 12px;
      cursor: pointer;
      font-size: 16px;
      line-height: 25px;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }

      .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 20px;
        width: 20px;
        border-radius: 50%;
        border: 1px solid #11bb2e;

        &:after {
          content: "";
          position: absolute;
          display: none;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: $color-secondary;
        }
      }

      input:checked ~ .checkmark:after {
        display: block;
      }
    }
    .background {
      background-position: center !important;
      background-size: cover !important;
      background-repeat: no-repeat !important;
    }
  }

  .checkout-title {
    margin-bottom: 10px;
    color: #fff;
    @media (max-width: 768px) {
      background-color: $bg-secondary;
    }
  }
  .background{
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }
</style>
